import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Lock, NotebookPen } from "lucide-react";
import { MarketingHeader } from "@/components/marketing-header";
import { apiUrl } from "@/lib/api-base";
import { coerceJournalTagForDisplay, type JournalLibraryTag } from "@/lib/journal-tags";
import { cn } from "@/lib/utils";

const TAG_COLORS: Record<JournalLibraryTag, string> = {
  Health: "card-sage",
  Finance: "card-golden",
  "Self-care": "card-blush",
  "Self-growth": "card-lavender",
  Tech: "card-sky",
  Other: "card-terra",
};

interface PublicJournal {
  id: string;
  title: string;
  tag: string;
  pullQuote: string | null;
  coreInsight: string;
  sourceTitle: string | null;
  backgroundImage: string | null;
  createdAt: string;
}

type LoadState = "loading" | "ready" | "not-found" | "error";

/**
 * Public, unauthenticated view for a shared journal link — the destination behind the
 * WhatsApp/X/LinkedIn/etc. share buttons on the journal detail page. Deliberately shows
 * only the curated subset the backend's GET /journals/:id/public returns (title, tag,
 * pull quote, core insight) — never the private free-write or next-step actions, which
 * stay behind login even when the entry itself is shared.
 */
export default function JournalSharePage() {
  const params = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>("loading");
  const [journal, setJournal] = useState<PublicJournal | null>(null);

  useEffect(() => {
    if (!params.id) return;
    let cancelled = false;
    fetch(apiUrl(`/journals/${params.id}/public`))
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setState("not-found");
          return;
        }
        if (!res.ok) {
          setState("error");
          return;
        }
        const data = (await res.json()) as PublicJournal;
        setJournal(data);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const tagKey = journal ? coerceJournalTagForDisplay(journal.tag) : "Other";
  const color = TAG_COLORS[tagKey];
  const quote = journal?.pullQuote?.trim() || journal?.coreInsight || "";

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />

      <div className="mx-auto w-full max-w-2xl px-5 py-16">
        {state === "loading" && (
          <div className="space-y-4">
            <div className="h-6 w-24 animate-pulse rounded-full bg-muted" />
            <div className="h-48 animate-pulse rounded-3xl bg-muted" />
          </div>
        )}

        {(state === "not-found" || state === "error") && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-4 py-20 text-center"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-lavender/25">
              <NotebookPen className="h-6 w-6 text-lavender-foreground" />
            </div>
            <div>
              <h1 className="font-display text-xl font-medium text-foreground">
                {state === "not-found" ? "This reflection isn't available anymore." : "Something went wrong loading this reflection."}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                It may have been deleted, or the link is incorrect.
              </p>
            </div>
            <a
              href="/"
              className="mt-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary/90"
            >
              Explore Unfold
            </a>
          </motion.div>
        )}

        {state === "ready" && journal && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div
              className={cn(
                "relative overflow-hidden rounded-3xl border border-white/60 p-6 sm:p-8",
                color,
              )}
            >
              <div className="pointer-events-none absolute right-0 top-0 h-32 w-32 -translate-y-1/3 translate-x-1/4 rounded-full bg-white/20 blur-2xl" />

              <div className="relative z-10">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-foreground/55">
                    {tagKey}
                  </span>
                  <span className="text-foreground/30">·</span>
                  <span className="text-[10px] text-foreground/45">
                    {format(new Date(journal.createdAt), "MMMM d, yyyy")}
                  </span>
                </div>

                <h1 className="font-display text-2xl font-semibold leading-snug text-foreground sm:text-3xl">
                  {journal.title}
                </h1>

                {quote && (
                  <p className="mt-4 font-serif text-lg italic leading-relaxed text-foreground/75">
                    "{quote}"
                  </p>
                )}

                {journal.sourceTitle && (
                  <p className="mt-4 text-xs text-foreground/45">from: {journal.sourceTitle}</p>
                )}
              </div>
            </div>

            <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
              <Lock className="h-3 w-3" />
              The full reflection stays private — this is just what was shared.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-muted-foreground">
                Unfold turns what you read and watch into reflections like this one.
              </p>
              <a
                href="/"
                className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary/90"
              >
                Start your own Unfold
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
